import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PUBLIC page — no auth. Rendered when `window.location.pathname` matches
 * `/reel/:slug`. Masonry layout of a user's ⭐ favorites.
 */
const PublicReelView = () => {
  const slug = (window.location.pathname.match(/^\/reel\/([^/?#]+)/) || [])[1] || '';
  const [reel, setReel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/reel/${slug}`);
        if (!cancelled) setReel(res.data);
      } catch (err) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">
        Loading reel…
      </div>
    );
  }

  if (notFound || !reel) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6">
        <div className="text-6xl mb-4">🎬</div>
        <h1 className="text-2xl font-bold mb-2">Reel not available</h1>
        <p className="text-slate-400 text-center max-w-md">
          This reel doesn't exist yet, or the creator hasn't made it public.
        </p>
        <a href="/" className="mt-6 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold">
          ← Back to AdElevate
        </a>
      </div>
    );
  }

  const items = reel.items || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100" data-testid="public-reel-view">
      {/* Header — clean, viewer-first */}
      <header className="max-w-6xl mx-auto px-4 md:px-6 pt-12 md:pt-20 pb-8">
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
          {reel.title || 'My Reel'}
        </h1>
        {reel.tagline && (
          <p className="mt-3 text-base md:text-lg text-slate-400">{reel.tagline}</p>
        )}
        <p className="mt-4 text-xs text-slate-500">{items.length} favorite{items.length === 1 ? '' : 's'}</p>
      </header>

      {/* Masonry grid via CSS columns — varied heights look great this way */}
      <main className="max-w-6xl mx-auto px-4 md:px-6 pb-12">
        {items.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <div className="text-4xl mb-3">✨</div>
            <p>Nothing to show yet.</p>
          </div>
        ) : (
          <div className="columns-2 sm:columns-3 lg:columns-4 gap-3 md:gap-4 space-y-3 md:space-y-4">
            {items.map(it => (
              <ReelTile key={`${it.kind}-${it.id}`} item={it} />
            ))}
          </div>
        )}
      </main>

      {/* Attribution footer */}
      <footer className="max-w-6xl mx-auto px-4 md:px-6 pb-10 pt-4 border-t border-slate-800/60">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>Made with <span className="text-emerald-400 font-semibold">AdElevate</span></p>
          <a
            href="/"
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold"
            data-testid="reel-cta-try-adelevate"
          >
            Try AdElevate →
          </a>
        </div>
      </footer>
    </div>
  );
};

const ReelTile = ({ item }) => {
  const [playing, setPlaying] = React.useState(false);
  const isVideo = item.media_type === 'video';

  // Video tiles use the poster image (companion still from the same campaign)
  // until the viewer clicks Play — this keeps the masonry snappy and uniform
  // instead of showing blank <video> boxes while metadata loads.
  if (isVideo && !playing) {
    return (
      <button
        type="button"
        onClick={() => setPlaying(true)}
        className="group relative block w-full break-inside-avoid rounded-2xl overflow-hidden bg-slate-800/40 border border-slate-800 shadow-lg hover:shadow-2xl transition-shadow"
      >
        {item.poster_url ? (
          <img src={item.poster_url} alt={item.title || 'ad'} className="w-full h-auto" loading="lazy" />
        ) : (
          <div className="w-full aspect-square bg-gradient-to-br from-emerald-600 via-teal-700 to-slate-900 flex items-end p-4">
            <p className="text-white/90 font-semibold text-sm leading-tight drop-shadow line-clamp-3">
              {item.title || 'Video ad'}
            </p>
          </div>
        )}
        {/* Play glyph */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
            <div className="w-0 h-0 border-l-[16px] border-l-slate-900 border-y-[11px] border-y-transparent ml-1.5"></div>
          </div>
        </div>
        <div className="absolute top-2 left-2 text-[10px] font-bold text-white px-2 py-0.5 rounded bg-red-500/90">VIDEO</div>
      </button>
    );
  }

  return (
    <div className="break-inside-avoid rounded-2xl overflow-hidden bg-slate-800/40 border border-slate-800 shadow-lg hover:shadow-2xl transition-shadow">
      {isVideo ? (
        <video
          src={item.url}
          poster={item.poster_url || undefined}
          className="w-full h-auto"
          muted playsInline loop controls autoPlay preload="metadata"
        />
      ) : (
        <img
          src={item.url}
          alt={item.title || 'ad'}
          className="w-full h-auto"
          loading="lazy"
        />
      )}
    </div>
  );
};

export default PublicReelView;
